import http from '../../../../services/HttpService'
import {
    updateInvite,
    revokeInvite,
    createPrivilege,
    revokePrivilege,
    updatePrivilege,
    createProjectCopy,
    generateDownloadKey,
    getUserDataForProject,
    getSuggestedCopiedProjectName
} from '../../../../services/ProjectService'

/**
 * Retrieve a single project
 *
 * @param  {Function} options.commit
 * @param  {Integer|String} projectId
 * @return {Promise}
 */
const getProject = ({ commit, rootState }) => {
    let id = rootState.route.params.projectId
    commit('GET_PROJECT')
    return http.get(`projects/${id}/`)
        .then(res => commit('GET_PROJECT_SUCCESS', res.data))
        .catch(err => commit('GET_PROJECT_FAILED', err))
}

/**
 * Retrieve the marketing payload for a given project
 *
 * @param  {Function} options.commit
 * @param  {Integer|String} projectId
 * @return {Promise}
 */
const getMarketingProject = ({ commit, rootState }) => {
    let id = rootState.route.params.projectId
    commit('GET_PROJECT')
    return http.get(`projects/${id}/marketing/`)
        .then(res => commit('GET_PROJECT_SUCCESS', res.data))
        .catch(err => {
            commit('GET_PROJECT_FAILED', err)
            return Promise.reject(err)
        })
}

/**
 * Set the project state
 *
 * @param  {Function} options.commit
 * @param  {Object} project
 * @return {Promise}
 */
const setProject = ({ commit }, project) => {
    return new Promise((resolve, reject) => {
        commit('GET_PROJECT_SUCCESS', project)
        resolve()
    })
}

/**
 * Reset the project data
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const resetProject = ({ commit }) => {
    return new Promise((resolve, reject) => {
        commit('RESET_PROJECT')
        resolve('reset')
    })
}

/**
 * Create a dynamic download URL and access the file
 *
 * @param  {Function} options.commit
 * @param  {Number} projectId
 * @return {Void}
 */
const downloadExcelFile = ({ commit }, projectId) => new Promise((resolve, reject) => {
    commit('DOWNLOAD_KEY_GET')
    generateDownloadKey(projectId)
        .then(res => {
            commit('DOWNLOAD_KEY_SUCCESS')
            commit('DOWNLOAD_EXCEL_SUCCESS', {
                filename: res.data.filename,
                url: res.data.url
            })
            resolve(res.data.url)
        })
        .catch(err => commit('DOWNLOAD_KEY_FAILED', err))
})

/**
 * Reset the state params for excel downloading
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const resetExcelContext = ({ commit }) => {
    commit('RESET_EXCEL_CONTEXT')
}

/**
 * Trigger the download success action
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const excelFileDownloadCompleted = ({ commit }) => {
    commit('EXCEL_FILE_DOWNLOADED')
}

/**
 * Reset the user context to its original state
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const resetState = ({ commit }) => {
    commit('RESET_STATE')
}

/**
 * Get the copied project name from the server and set the state property
 *
 * @param  {Function} options.commit
 * @param  {Number} id
 * @return {Void}
 */
const setCopyName = ({ commit }, id) => {
    commit('GET_COPY_NAME')
    setTimeout(() => {
        getSuggestedCopiedProjectName(id)
            .then(res => commit('GET_COPY_NAME_SUCCESS', res.data.data.name))
            .catch(err => commit('GET_COPY_NAME_FAILURE', err))
    }, 1000)
}

/**
 * Reset the copy name state
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const resetCopyName = ({ commit }) => {
    commit('RESET_COPY_NAME')
}

/**
 * Update the copied project name
 *
 * @param  {Function} options.commit
 * @param  {String} updatedName
 * @return {Void}
 */
const updateName = ({ commit }, updatedName) => {
    commit('UPDATE_COPY_NAME', updatedName)
}

/**
 * Create a copy of an existing project
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const createCopy = ({ commit, state }, id) => {
    commit('SAVING_COPY')
    createProjectCopy(id, state.copy_name)
        .then(res => commit('SAVING_COPY_SUCCESS', res))
        .catch(() => commit('SAVING_COPY_FAILURE'))
}

/**
 * Reset the state
 *
 * @param  {Object} options.commit
 * @return {Void}
 */
const resetCopyState = ({ commit }) => {
    commit('RESET_COPY_STATE')
}

/**
 * Retrieve the user management data
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const getUserManagementData = ({ commit }, id) => {
    return getUserDataForProject(id)
        .then(res => {
            let data = res.data
            commit('USER_MANAGEMENT_GET_SUCCESS', data)
            return Promise.resolve(data)
        })
        .catch(err => {
            console.log(err)
            return Promise.reject(err)
        })
}

/**
 * Update a privilege for a given user for a given project
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const updateUserPrivilege = ({ commit, dispatch }, payload) => {
    let level = payload.level
    let privilege = payload.privilegeId
    let project = payload.projectId

    updatePrivilege(project, privilege, level)
        .then(commit('PRIVILEGE_UPDATED_SUCCESS', payload))
        .then(() => {
            dispatch('addNotification', {
                type: 'success',
                title: 'Success',
                text: 'User prviilege has been successfully updated.',
                timeout: true
            }, { root: true })
        })
        .catch(err => console.log(err))
}

const revokeUserPrivilege = ({ commit }, payload) => {
    let privilege = payload.privilegeId
    let project = payload.projectId
    let confirmed = window.confirm('Are you sure you want to revoke this user\'s privilege?')

    if (confirmed) {
        revokePrivilege(project, privilege)
            .then(commit('PRIVILEGE_REVOKED_SUCCESS', privilege))
            .catch(err => console.log(err))
    }
}

/**
 * Generate a new project privilege
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const createProjectPrivilege = ({ commit }, payload) => {
    return createPrivilege(payload.projectId, payload.data)
        .then(res => {
            commit('NEW_PRIVILEGE_SUCCESS', payload)
            return res
        })
        .catch(err => console.log(err))
}

/**
 * Revoke a invitation
 *
 * @param  {Function} options.commit
 * @param  {Number} inviteId
 * @return {Promise}
 */
const revokeInvitation = ({ commit }, inviteId) => {
    let confirmed = window.confirm('Are you sure you want to revoke this invitation?')
    if (confirmed) {
        return revokeInvite(inviteId)
            .then(res => {
                commit('INVITE_REMOVED_SUCCESS', inviteId)
                return res
            })
            .catch(err => console.log(err))
    }
}

/**
 * Request the invitation level be modified
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Void}
 */
const updateInvitation = ({ commit, dispatch }, payload) => {
    updateInvite(payload.invitationId, payload.level)
        .then(res => commit('UPDATE_INVITE_LEVEL', payload))
        .then(() => {
            dispatch('addNotification', {
                type: 'success',
                title: 'Success',
                text: 'User\'s invitation privilege has been successfully updated.',
                timeout: true
            }, { root: true })
        })
        .catch(err => console.log(err))
}

/**
 * Get all versions of a given project.
 *
 * @param  {Function} options.commit
 * @param  {Object} options.rootState
 * @return {Void}
 */
const getVersionHistory = ({ commit, rootState }) => {
    let id = rootState.route.params.projectId
    http.get(`projects/${id}/versions/`)
        .then(res => commit('GET_HISTORY_SUCCESS', res.data))
        .catch(err => console.log(err))
}

/**
 * Get the current version of a project
 *
 * @param  {Function} options.commit
 * @param  {Object} options.rootState
 * @return {Void}
 */
const getCurrentVersion = ({ commit, rootState }) => {
    let id = rootState.route.params.projectId
    http.get(`projects/${id}/versions/current/`)
        .then(res => commit('GET_CURRENT_VERSION', res.data))
        .catch(err => console.log(err))
}

/**
 * Change the current project version
 *
 * @param  {Function} options.commit
 * @param  {Object} version
 * @return {Void}
 */
const changeProjectVersion = ({ commit, rootState, dispatch }, version) => {
    let id = rootState.route.params.projectId
    http.patch(`projects/${id}/versions/current/?version=${version}`)
        .then(() => dispatch('getProject'))
        .then(() => dispatch('getCurrentVersion'))
        .then(() => dispatch('getVersionHistory'))
        .then(() => {
            dispatch('addNotification', {
                type: 'success',
                title: 'Success',
                text: 'Project version successfully changed.',
                timeout: true
            }, { root: true })
        })
        .catch(err => console.log(err))
}

/**
 * Update the project name
 *
 * @param  {Function} options.commit
 * @param  {String} name
 * @return {Void}
 */
const updateProjectName = ({ commit }, name) => commit('UPDATE_PROJECT_NAME', name)

export default {
    getProject,
    updateName,
    setProject,
    resetState,
    createCopy,
    setCopyName,
    resetProject,
    resetCopyName,
    resetCopyState,
    updateInvitation,
    revokeInvitation,
    getCurrentVersion,
    downloadExcelFile,
    resetExcelContext,
    getVersionHistory,
    updateProjectName,
    getMarketingProject,
    updateUserPrivilege,
    revokeUserPrivilege,
    changeProjectVersion,
    getUserManagementData,
    createProjectPrivilege,
    excelFileDownloadCompleted
}



// WEBPACK FOOTER //
// ./src/state/modules/projects/single/actions.js